// ** React Imports
import { createContext, useEffect, useState } from 'react'

// ** Next Import
import { useNavigate, useSearchParams } from 'react-router-dom'

// ** Axios
import axios from 'axios'

// ** Config
import authConfig from '../configs/auth'
import FallbackSpinner from 'src/@core/components/spinner'
import { ApiEndPoints } from 'src/network/endpoints'
import { FaceMan } from 'mdi-material-ui'
import { axiosInstance } from 'src/network/adapter'
import socket from 'src/socket'

// ** Defaults
const defaultProvider = {
  user: null,
  loading: true,
  fcmToken: '',
  setUser: () => null,
  setFcmToken: () => null,
  setLoading: () => Boolean,
  isInitialized: false,
  updateUser: () => null,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setIsInitialized: () => Boolean,
  register: () => Promise.resolve(),
  rolePermissions: [],
  setRolePermission: () => []
}
const AuthContext = createContext(defaultProvider)

const AuthProvider = ({ children }) => {
  // ** States
  const [user, setUser] = useState(defaultProvider.user)
  const [loading, setLoading] = useState(defaultProvider.loading)
  const [isInitialized, setIsInitialized] = useState(defaultProvider.isInitialized)
  const [rolePermissions, setRolePermission] = useState(defaultProvider.rolePermissions)
  const [fcmToken, setFcmToken] = useState(defaultProvider.fcmToken)
  // ** Hooks
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const updateUser = updatedUserData => {
    const newUser = { ...user, ...updatedUserData }
    setUser(newUser)
    window.localStorage.setItem(authConfig.storageUserDataKeyName, JSON.stringify(newUser))
  }

  useEffect(() => {
    const initAuth = async () => {
      setIsInitialized(true)
      const storedToken = window.localStorage.getItem(authConfig.storageTokenKeyName)
      if (storedToken) {
        setLoading(true)
        await axios
          .get(ApiEndPoints.AUTH.me, {
            headers: {
              Authorization: `Bearer ${storedToken}` // storedToken
            }
          })
          .then(async response => {
            setLoading(false)
            let user = { ...response.data.data.user }
            setUser({ ...user })
            window.localStorage.setItem(authConfig.storageUserDataKeyName, JSON.stringify(user))

            // Initialize agent socket events on session restore
            initializeAgentSocket(user._id)
          })
          .catch(() => {
            localStorage.removeItem(authConfig.storageUserDataKeyName)
            localStorage.removeItem(authConfig.storageTokenKeyName)
            setUser(null)
            setLoading(false)
          })
      } else {
        setLoading(false)
      }
    }
    initAuth()
  }, [])

  // Initialize agent socket events
  const initializeAgentSocket = userId => {
    if (!userId) return

    // Connect socket if not connected
    if (!socket.connected) {
      socket.io.opts.query = { userId }
      socket.connect()
    }

    // Wait for connection if needed, then emit events
    if (socket.connected) {
      socket.emit('userOnline', { userId })
      socket.emit('markAsDelivered', { role: 'users' })
    } else {
      socket.once('connect', () => {
        socket.emit('userOnline', { userId })
        socket.emit('markAsDelivered', { role: 'users' })
      })
    }
  }

  const handleLogin = async ({ token, user }) => {
    window.localStorage.setItem(authConfig.storageTokenKeyName, token)
    window.localStorage.setItem(authConfig.storageUserDataKeyName, JSON.stringify(user)) // Store user data
    setUser(user)

    // Initialize agent socket events on login
    initializeAgentSocket(user._id)

    const redirectUrl = searchParams.get('redirect')
    const isSafeRedirect =
      typeof redirectUrl === 'string' &&
      redirectUrl.startsWith('/') &&
      !redirectUrl.startsWith('//') &&
      !redirectUrl.split('/')[0].includes(':')
    navigate(isSafeRedirect ? redirectUrl : '/dashboard')
  }

  const handleLogout = async () => {
    try {
      await axiosInstance.post(ApiEndPoints.AUTH.logout)
    } catch (error) {
      // Intentionally ignore API errors on logout; proceed to clear local state
    } finally {
      setUser(null)
      window.localStorage.removeItem(authConfig.storageUserDataKeyName)
      window.localStorage.removeItem(authConfig.storageTokenKeyName)
      navigate('/login')
    }
  }

  const handleRegister = () => {}

  const values = {
    fcmToken,
    setLoading,
    setFcmToken,
    user,
    loading,
    setUser,
    updateUser,
    isInitialized,
    setIsInitialized,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister,
    rolePermissions,
    setRolePermission
  }

  return <AuthContext.Provider value={values}>{loading ? <FallbackSpinner /> : children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
